import {
  AsyncPipe,
  CurrencyPipe,
  NgFor,
  NgIf,
  NgStyle,
  NgSwitch,
  NgSwitchCase,
  NgTemplateOutlet
} from '@angular/common';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { ExtendedModule } from '@angular/flex-layout/extended';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup
} from '@angular/forms';
import { MatAnchor, MatButton, MatIconButton } from '@angular/material/button';
import {
  MatCard,
  MatCardActions,
  MatCardContent
} from '@angular/material/card';
import { MatOption } from '@angular/material/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { ColDef, FirstDataRenderedEvent, GridOptions } from 'ag-grid-community';
import { DateFormatPipe } from 'ngx-moment';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';
import { Booking } from '../models/booking';
import { DocketService } from '../shared/';
import { CustomerNameCellComponent } from '../shared/ag-grid-cells/customer-name-cell.component';
import { DocketNoAccRefPreviewCellComponent } from '../shared/ag-grid-cells/docket-no-acc-ref-preview-cell.component';
import { HireItemNameItemsCellComponent } from '../shared/ag-grid-cells/hire-item-name-items-cell.component';
import { HireItemOffHireWithEstimateCellComponent } from '../shared/ag-grid-cells/hire-item-off-hire-with-estimate-cell.component';
import { BookingListComponent } from '../shared/bookings/bookings-list/booking-list.component';
import { DeliveryListComponent } from '../shared/deliveries/delivery-list/delivery-list.component';
import { DeliveryEstimateDialogComponent } from '../shared/dialogs/delivery-dialog/delivery-estimate-dialog/delivery-estimate-dialog.component';
import { NewTransactionDialogComponent } from '../shared/dialogs/new-transaction-dialog/new-transaction-dialog.component';
import { TestIdDirective } from '../shared/directives/test-id.directive';
import { contractIdComparator } from '../shared/helpers/ContractIdComparator';
import { customerNameComparatorFactory } from '../shared/helpers/CustomerNameComparatorFactory';
import { dateComparator } from '../shared/helpers/DateComparator';
import { filterDateComparator } from '../shared/helpers/FilterDateComparator';
import { BookingItemNamePipe } from '../shared/pipes/booking-item-name.pipe';
import { CustomerNamePipe } from '../shared/pipes/customer-name.pipe';
import { DeliveryItemNamePipe } from '../shared/pipes/delivery-item-name.pipe';
import { HlCurrencyPipe } from '../shared/pipes/hl-currency.pipe';
import { BookingService } from '../shared/services/booking.service';
import { ConfigService } from '../shared/services/config.service';
import {
  ActiveDocket,
  DashboardService
} from '../shared/services/dashboard.service';
import { DeliveryService } from '../shared/services/delivery.service';
import {
  DeliveryJobForUser,
  UserJobsService
} from '../shared/services/user-jobs.service';
import { dashboardStatsCards } from './dashboardStatCards';
import { MyJobsComponent } from './my-jobs-tile/my-jobs/my-jobs.component';
import { PendingPaymentListComponent } from './pending-payment-list/pending-payment-list.component';
import { ContactService } from '../shared/services/contact.service';

@Component({
  selector: 'dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
  providers: [
    DocketService,
    DateFormatPipe,
    BookingService,
    DeliveryService,
    BookingItemNamePipe,
    DeliveryItemNamePipe,
    DashboardService,
    CurrencyPipe,
    CustomerNamePipe,
    HlCurrencyPipe,
    BookingItemNamePipe,
    CustomerNamePipe,
    DeliveryItemNamePipe,
    DateFormatPipe,
    HlCurrencyPipe,
    UserJobsService,
    ContactService
  ],
  standalone: true,
  imports: [
    MatAnchor,
    RouterLink,
    MatButton,
    TestIdDirective,
    NgIf,
    MatCard,
    MatCardContent,
    NgFor,
    MatCardActions,
    MyJobsComponent,
    NgSwitch,
    NgSwitchCase,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    AgGridModule,
    NgStyle,
    ExtendedModule,
    PendingPaymentListComponent,
    BookingListComponent,
    FlexModule,
    DeliveryListComponent,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatSelect,
    MatOption,
    AsyncPipe,
    HlCurrencyPipe,
    BookingItemNamePipe,
    CustomerNamePipe
  ]
})
export class DashboardComponent implements OnInit, OnDestroy {
  @ViewChild('tableContainer', { static: true }) tableContainer;
  @ViewChild('agGrid') grid!: AgGridAngular;

  columns: ColDef[] = [];
  locations = [];
  users = [];
  gridOptions: GridOptions = {
    headerHeight: 45,
    onGridReady: this.gridReady.bind(this),
    columnMenu: 'new',
    onFirstDataRendered(event: FirstDataRenderedEvent<any>) {
      event.columnApi.autoSizeAllColumns(false);
    }
  };

  height = 400;

  private deliveries$ = new BehaviorSubject<DeliveryJobForUser[]>([]);
  private dockets$ = new BehaviorSubject<ActiveDocket[]>([]);
  private bookings$ = new BehaviorSubject<Booking[]>([]);

  filterForm = new UntypedFormGroup({
    customer: new UntypedFormControl(''),
    location: new UntypedFormControl(0),
    user: new UntypedFormControl(0)
  });

  filteredDeliveries$ = combineLatest([
    this.deliveries$,
    this.filterForm.valueChanges.pipe(startWith(this.filterForm.value))
  ]).pipe(map(([deliveries, _]) => this.filterDeliveries(deliveries)));

  filteredDockets$ = combineLatest([
    this.dockets$,
    this.filterForm.valueChanges.pipe(startWith(this.filterForm.value))
  ]).pipe(map(([dockets, _]) => this.filterDockets(dockets)));

  filteredBookings$ = combineLatest([
    this.bookings$,
    this.filterForm.valueChanges.pipe(startWith(this.filterForm.value))
  ]).pipe(map(([bookings, _]) => this.filterBookings(bookings)));

  _selectedView: string;
  private ngUnsubscribe = new Subject<void>();
  showFilter = false;
  selectedFilter = null;
  easyFilterSelected: string;

  statCardDefs = dashboardStatsCards;

  statsProcessed$: Observable<any[]>;
  pendingFilterSource = new BehaviorSubject<string>('all');
  pendingFilter$ = this.pendingFilterSource.asObservable();

  private mapStatValue(path: string, stats: any): string | number {
    return path.split('.').reduce((acc, cur) => acc[cur] ?? 0, stats);
  }

  showFilteredView(viewName, filterName, $event) {
    switch (viewName) {
      case 'active':
        this.showActiveWithFilter(filterName, $event);
        break;
      case 'bookings':
        this.showBookingsWithFilter(filterName, $event);
        break;
      case 'deliveries':
        this.showDeliveriesWithFilter(filterName, $event);
        break;
      case 'pending':
        this.showPendingWithFilter(filterName, $event);
        break;
      case 'workshop':
        this.router.navigate(['workshop'], {
          queryParams: { status: filterName }
        });
        break;
    }
  }

  set selectedView(value) {
    this._selectedView = value;
    if (value) {
      this.router.navigate(['dashboard', { tab: this._selectedView }], {
        queryParamsHandling: 'merge'
      });
      return;
    }
    this.showFilter = false;
    this.selectedFilter = null;
    this.easyFilterSelected = null;
    this.router.navigate(['dashboard']);
  }

  constructor(
    private datePipe: DateFormatPipe,
    private router: Router,
    private route: ActivatedRoute,
    public dialog: MatDialog,
    private configService: ConfigService,
    private customerNamePipe: CustomerNamePipe,
    private bookingService: BookingService,
    private deliveryService: DeliveryService,
    private dashboardService: DashboardService
  ) {}

  ngOnInit() {
    this.configService.dashboardFilterConfig
      .pipe(take(1), takeUntil(this.ngUnsubscribe))
      .subscribe((filter) => {
        this.filterForm.patchValue(filter);
      });
    this.filterForm.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(({ location, user }) =>
        this.configService.setDashboardConfig({
          location,
          user
        })
      );

    this.statsProcessed$ = this.getDashboardStats();
    this.configService.locations.subscribe((locations) => {
      if (locations) {
        this.locations = locations;
      }
    });
    this.configService.allUsers.subscribe((users) => {
      if (users) {
        this.users = Object.assign([], users);
        this.users.unshift({ id: 0, name: 'All Users' });
      }
    });

    this.route.params.subscribe((params) => {
      if (params['tab']) {
        if (params['tab'] !== this._selectedView) {
          this.selectedView = params['tab'];
        }
        this.getTabData();
      } else {
        this.selectedView = null;
      }
    });

    this.getDashboardConfig();
    this.setupTables();
    this.showDefaultTab();
  }

  private getDashboardStats(): Observable<any[]> {
    return this.configService.userConfig.pipe(
      filter(Boolean),
      map(({ dashboards_visible }) => dashboards_visible?.split(',') ?? []),
      takeUntil(this.ngUnsubscribe),
      switchMap((dashboardsVisible) =>
        this.dashboardService.getDashboardStats().pipe(
          takeUntil(this.ngUnsubscribe),
          map((stats) => {
            const cardsToShow = this.statCardDefs.filter(
              ({ link }) =>
                dashboardsVisible.length === 0 ||
                dashboardsVisible.includes(link)
            );
            return cardsToShow.map((card) => {
              return {
                ...card,
                stats: card.stats.map(({ path, ...rest }) => {
                  const value = path ? this.mapStatValue(path, stats) : null;
                  return {
                    value,
                    ...rest,
                    path
                  };
                }),
                bigFigureValue: card.bigFigurePath
                  ? this.mapStatValue(card.bigFigurePath, stats)
                  : null
              };
            });
          }),
          mergeMap((cards) => {
            return this.dashboardService
              .getCustomDashboardTiles()
              .pipe(map((customTiles) => [...cards, ...customTiles]));
          })
        )
      )
    );
  }

  openDeliveryEstimator(event: MouseEvent) {
    this.dialog.open(DeliveryEstimateDialogComponent);
    event.stopPropagation();
  }

  getActive() {
    return this.dashboardService.getActiveDockets();
  }

  getBookings() {
    return this.bookingService
      .getAll(['items.item', 'items.product', 'items.category', 'customer'])
      .pipe(
        map((bookings) => {
          let filtered = bookings
            .map((booking) => {
              booking.items = booking.items.filter((item) => !item.deleted_at);
              return booking;
            })
            .filter((booking) => !booking.deleted_at && booking.items.length);
          return filtered;
        })
      );
  }

  getDeliveries() {
    return this.deliveryService.getPending().pipe(
      map((deliveries) => {
        if (this.selectedFilter) {
          return this.selectedFilter(deliveries);
        }
        return deliveries;
      })
    );
  }

  filtersUpdated() {
    this.filterForm.valueChanges
      .pipe(
        distinctUntilChanged(
          (prev, current) =>
            prev.location !== current.location || prev.user !== current.user
        ),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe(({ user, location }) => {
        this.configService.updateUserConfig('dashboard', {
          selectedUser: user,
          selectedLocation: location
        });
      });
  }

  private filterDockets(dockets: ActiveDocket[]): ActiveDocket[] {
    const { customer, user, location } = this.filterForm.value as {
      customer: string;
      user: number;
      location: number;
    };
    return dockets.filter((docket) => {
      if (user && docket.user_id !== user) {
        return false;
      }
      if (location && docket.location_out !== location) {
        return false;
      }
      if (
        customer &&
        docket.customer.full_name
          .toLowerCase()
          .indexOf(customer.toLowerCase()) === -1
      ) {
        return false;
      }
      if (this.selectedFilter) {
        return this.selectedFilter([docket]).length > 0;
      }
      return true;
    });
  }

  private filterBookings(bookings: Booking[]): Booking[] {
    const { customer, user, location } = this.filterForm.value as {
      customer: string;
      user: number;
      location: number;
    };
    return bookings.filter((booking) => {
      if (user && booking.user_id !== user) {
        return false;
      }
      if (
        location &&
        booking.items.every(({ location_id }) => location_id !== location)
      ) {
        return false;
      }
      if (
        customer &&
        booking.customer.full_name
          .toLowerCase()
          .indexOf(customer.toLowerCase()) === -1
      ) {
        return false;
      }
      if (this.selectedFilter) {
        return this.selectedFilter([booking]).length > 0;
      }
      return true;
    });
  }

  private filterDeliveries(
    deliveries: DeliveryJobForUser[]
  ): DeliveryJobForUser[] {
    const { customer, user, location } = this.filterForm.value as {
      customer: string;
      user: number;
      location: number;
    };
    return deliveries.filter((item: DeliveryJobForUser) => {
      if (user > 0 && user !== item.created_by) {
        return false;
      }
      if (location > 0 && location !== parseInt(item.location, 10)) {
        return false;
      }
      if (
        customer &&
        item.customer.toLowerCase().indexOf(customer.toLowerCase()) === -1
      ) {
        return false;
      }

      return true;
    });
  }

  clearFilters() {
    this.filterForm.reset({
      location: 0,
      user: 0,
      customer: ''
    });
    if (this.grid) {
      this.grid.api.setFilterModel(null);
    }
  }

  newHireContract() {
    const config: MatDialogConfig = {
      minWidth: 400,
      maxHeight: '80vh'
    };
    this.dialog.open(NewTransactionDialogComponent, config);
  }

  getTabData() {
    if (this._selectedView === 'active') {
      this.getActive()
        .pipe(take(1))
        .subscribe((dockets) => this.dockets$.next(dockets));
    }
    if (this._selectedView === 'bookings') {
      this.getBookings()
        .pipe(take(1))
        .subscribe((bookings) => this.bookings$.next(bookings));
    }
    if (this._selectedView === 'deliveries') {
      this.getDeliveries()
        .pipe(take(1))
        .subscribe((deliveries) => this.deliveries$.next(deliveries));
    }
  }

  gridReady(event) {
    if (this.gridOptions && this.gridOptions.api) {
      this.grid.api.autoSizeAllColumns();
    }
  }

  private setupTables() {
    this.columns = [
      {
        headerName: 'Status',
        field: 'status',
        filter: 'agTextColumnFilter',
        sort: 'desc',
        width: 180,
        resizable: true,
        cellClass: (params) => this.getStatusCellClass(params)
      },
      {
        headerName: 'Contract',
        cellRenderer: DocketNoAccRefPreviewCellComponent,
        filter: 'agTextColumnFilter',
        width: 150,
        valueGetter: ({ data }: { data: ActiveDocket }) => ({
          id: data.docket_id,
          account_reference: data.invoice_number
        }),
        resizable: true,
        comparator: contractIdComparator,
        filterValueGetter: ({ data }: { data: ActiveDocket }) =>
          `${data.docket_id} (${data.invoice_number})`
      },
      {
        headerName: 'Customer',
        cellRenderer: CustomerNameCellComponent,
        field: 'customer',
        width: 240,
        sortable: true,
        comparator: customerNameComparatorFactory(this.customerNamePipe),
        resizable: true,
        filter: 'agTextColumnFilter',
        filterValueGetter: (params: any) => {
          return params.data.customer.full_name;
        }
      },
      {
        headerName: 'Contact',
        field: 'contact_name',
        width: 240,
        sortable: true,
        resizable: true,
        filter: 'agTextColumnFilter',
        filterValueGetter: (params: any) => {
          return params.data.contact_name;
        }
      },
      {
        headerName: 'On Hire',
        field: 'item.on_hire',
        width: 180,
        resizable: true,
        valueGetter: (params) => {
          return this.datePipe.transform(
            params.data['on_hire'],
            ConfigService.dates.shortDateTimeFormat
          );
        },
        sortable: true,
        comparator: dateComparator,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: filterDateComparator
        }
      },
      {
        headerName: 'Off Hire',
        field: 'off_hire',
        resizable: true,
        width: 180,
        sortable: true,
        valueGetter: (params) => {
          return this.datePipe.transform(
            params.data['off_hire'] || params.data['estimated_off_hire'],
            ConfigService.dates.shortDateTimeFormat
          );
        },
        comparator: dateComparator,
        cellRenderer: HireItemOffHireWithEstimateCellComponent,
        filter: 'agDateColumnFilter',
        filterParams: {
          comparator: filterDateComparator
        }
      },
      {
        headerName: 'Item',
        cellRenderer: HireItemNameItemsCellComponent,
        filterValueGetter: (params: any) => {
          return `${params.data.quantity || 1} x ${params.data.item_name}`;
        },
        filter: 'agTextColumnFilter',
        width: 400
      },
      {
        headerName: 'Purchase Order',
        field: 'purchase_order',
        filter: 'agTextColumnFilter',
        width: 150,
        resizable: true
      }
    ];
    // this.height = window.innerHeight - this.tableContainer.nativeElement.getBoundingClientRect().top - 340;
    this.height = 600;
  }

  getStatusCellClass(params: any) {
    if (params.value === 'Stale') {
      return ['bg-red-600', 'text-white', 'text-center'];
      // return { backgroundColor: 'red !important', color: 'white' };
    }
    if (params.value === 'Overdue') {
      return ['bg-red-300', 'text-center'];
      // return { backgroundColor: 'orange !important', color: 'white' };
    }

    return ['text-center'];
  }

  private getDashboardConfig() {
    this.configService.userConfig.pipe(take(1)).subscribe((config) => {
      if (!config.dashboard) {
        return;
      }
      const { selectedUser, selectedLocation } = config.dashboard;
      if (selectedUser && this.filterForm.value['user'] !== selectedUser) {
        this.filterForm.get('user').patchValue(selectedUser);
      }
      if (
        selectedLocation &&
        this.filterForm.value['location'] !== selectedLocation
      ) {
        this.filterForm.get('location').patchValue(selectedLocation);
      }
    });
  }

  private showDefaultTab() {
    this.configService.userConfig
      .pipe(
        filter(
          (config) =>
            ['active', 'pending', 'bookings', 'deliveries'].indexOf(
              config?.default_dashboard
            ) > -1
        ),
        map(({ default_dashboard }) => default_dashboard),
        distinctUntilChanged()
      )
      .subscribe((defaultTab) => {
        this.showView(defaultTab);
      });
  }

  showActiveWithFilter(filterType, event) {
    switch (filterType) {
      case 'overdue':
        this.selectedFilter = this.dashboardService.filterOverdue;
        this.easyFilterSelected = 'Return Overdue';
        break;
      case 'today':
        this.selectedFilter = this.dashboardService.filterReturningToday;
        this.easyFilterSelected = 'Returning Today';
        break;
      case 'unknown':
        this.selectedFilter = this.dashboardService.filterUnknown;
        this.easyFilterSelected = 'Unknown Return Date';
        break;
    }
    this.selectedView = 'active';
    event.stopPropagation();
    event.preventDefault();
  }

  showBookingsWithFilter(filterType, event) {
    switch (filterType) {
      case 'overdue':
        this.selectedFilter = this.dashboardService.bookingsFilterOverdue;
        this.easyFilterSelected = 'Overdue';
        break;
      case 'today':
        this.selectedFilter = this.dashboardService.bookingsFilterToday;
        this.easyFilterSelected = `Today's`;
        break;
      case 'week':
        this.selectedFilter = this.dashboardService.bookingsFilterThisWeek;
        this.easyFilterSelected = `This week's`;
        break;
      case 'awaiting':
        this.selectedFilter = this.dashboardService.bookingsFilterAwaiting;
        this.easyFilterSelected = `Awaiting Customer Response`;
        break;
      case 'confirmed':
        this.selectedFilter = this.dashboardService.bookingsFilterConfirmed;
        this.easyFilterSelected = `Confirmed`;
        break;
      case 'request':
        this.selectedFilter = this.dashboardService.bookingsFilterRequests;
        this.easyFilterSelected = `Requests`;
        break;
      case 'stockWarnings':
        this.selectedFilter = this.dashboardService.bookingsFilterStockWarnings;
        this.easyFilterSelected = `Stock Warnings`;
        break;
    }
    this.selectedView = 'bookings';
    event.stopPropagation();
    event.preventDefault();
  }

  showDeliveriesWithFilter(filterType, event) {
    switch (filterType) {
      case 'overdue':
        this.selectedFilter = this.dashboardService.deliveriesFilterOverdue;
        this.easyFilterSelected = 'Overdue';
        break;
      case 'today':
        this.selectedFilter = this.dashboardService.deliveriesFilterToday;
        this.easyFilterSelected = `Today's`;
        break;
      case 'week':
        this.selectedFilter = this.dashboardService.deliveriesFilterThisWeek;
        this.easyFilterSelected = `This week's`;
        break;
    }
    this.selectedView = 'deliveries';
    event.stopPropagation();
    event.preventDefault();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  showView(view) {
    if (view === 'workshop') {
      this.router.navigate(['/workshop']);
      return;
    }
    if (this._selectedView !== view) {
      this.selectedView = view;
    }
  }

  toggleFilter() {
    this.showFilter = !this.showFilter;
    if (!this.grid) {
      return;
    }
    const newColDefs = this.grid.api.getColumnDefs().map((col) => {
      if ('filter' in col && col.filter) {
        col.floatingFilter = this.showFilter;
      }
      return col;
    });
    this.grid.api.setGridOption('columnDefs', newColDefs);
  }

  removeEasyFilter() {
    this.easyFilterSelected = null;
    this.selectedFilter = null;
    this.getTabData();
  }

  private showPendingWithFilter(filterName, $event) {
    this.selectedView = 'pending';
    this.pendingFilterSource.next(filterName);
  }
}
